import { Fragment, useState, useEffect } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import { isHtmlArtifact } from "../../components/CertificateArtwork/CertificateArtwork";
import HtmlArtifactFrame from "../../components/CertificateArtwork/HtmlArtifactFrame";
import { DownloadIcon, XIcon } from "../../components/icons";
import EntityConstants from "../../configs/EntityConstants";
import ZoomInIcon from "../../resources/icons/zoom-in.svg";
import ZoomOutIcon from "../../resources/icons/zoom-out.svg";
import Toast from "../Toast/Toast";

export default function ImagePreviewModal({
  isOpen,
  onClose,
  imageUrl,
  artifactType,
  downloadUrl,
  downloadFilename,
  credentialId,
  testId = "image-preview-modal",
}) {
  const { t } = useTranslation();

  const [showPercentage, setShowPercentage] = useState(false);
  const [scale, setScale] = useState(1);

  const handleDownload = async () => {
    if (downloadUrl) {
      try {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download =
          downloadFilename ||
          (credentialId ? `${credentialId}.png` : "certificate.png");
        document.body.appendChild(link);

        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
        try {
          if (credentialId) {
            const fallbackUrl = `${EntityConstants.DOWNLOAD_SVG_AS_PNG}${credentialId}`;
            const fallbackLink = document.createElement("a");
            fallbackLink.href = fallbackUrl;
            fallbackLink.download = downloadFilename || `${credentialId}.png`;
            document.body.appendChild(fallbackLink);
            fallbackLink.click();
            document.body.removeChild(fallbackLink);
            return;
          }
        } catch (e) {
          // ignore and fall through to toast below
        }

        Toast({
          type: "error",
          title: t("history:error"),
          message: t("history:failed_download_certificate"),
        });
      }
    } else {
      Toast({
        type: "error",
        title: t("history:error"),
        message: t("history:download_url_not_available"),
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      setScale(1);
    }
  }, [isOpen]);

  useEffect(() => {
    let timer;
    if (showPercentage) {
      timer = setTimeout(() => {
        setShowPercentage(false);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [showPercentage]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            id={`${testId}-backdrop`}
            className="fixed inset-0 bg-black/75 backdrop-blur-sm"
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id={testId}
                className="w-full max-w-6xl transform overflow-hidden rounded-lg bg-cfSecondary text-left align-middle shadow-xl transition-all"
              >
                <div className="relative">
                  <button
                    id={`${testId}-close`}
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute right-4 top-2 z-10 p-2 rounded-full transition-colors focus:outline-none"
                  >
                    <XIcon size={20} className="inline-flex" aria-hidden />
                  </button>

                  {/* Updated container with fixed dimensions */}
                  <div className="h-[93vh] w-full relative">
                    <TransformWrapper
                      initialScale={1}
                      minScale={0.5}
                      maxScale={3}
                      wheel={{ step: 0.1 }}
                      onZoom={(ref) => {
                        setScale(ref.state.scale);
                        setShowPercentage(true);
                      }}
                    >
                      {({ zoomIn, zoomOut, resetTransform }) => (
                        <>
                          <TransformComponent
                            wrapperStyle={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                            contentStyle={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <div
                              role="button"
                              tabIndex={0}
                              style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                              onClick={() => {
                                if (scale !== 1) {
                                  resetTransform();
                                  setScale(1);
                                  setShowPercentage(true);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  if (scale !== 1) {
                                    resetTransform();
                                    setScale(1);
                                    setShowPercentage(true);
                                  }
                                }
                              }}
                            >
                              {isHtmlArtifact(artifactType) ? (
                                <HtmlArtifactFrame
                                  url={imageUrl}
                                  title="Certificate Preview"
                                  fit="contain"
                                  className="h-full"
                                  frameStyle={{ pointerEvents: "none" }}
                                />
                              ) : (
                                <img
                                  src={imageUrl}
                                  alt="Certificate Preview"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                    pointerEvents: "none",
                                  }}
                                />
                              )}
                            </div>
                          </TransformComponent>

                          {showPercentage && (
                            <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black/50 text-cfSecondary px-3 py-1 rounded-md text-sm transition-opacity duration-300">
                              {Math.round(scale * 100)}%
                            </div>
                          )}

                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-4 bg-cfSecondary rounded-full p-2 shadow-lg">
                            <button
                              id={`${testId}-zoom-in`}
                              onClick={() => {
                                zoomIn();
                                setShowPercentage(true);
                              }}
                              className="p-2 rounded-full hover:bg-cfCardHover transition-colors"
                              title="Zoom In"
                            >
                              <img
                                src={ZoomInIcon}
                                alt="Zoom In"
                                className="w-5 h-5"
                              />
                            </button>
                            <button
                              id={`${testId}-zoom-out`}
                              onClick={() => {
                                zoomOut();
                                setShowPercentage(true);
                              }}
                              className="p-2 rounded-full hover:bg-cfCardHover transition-colors"
                              title="Zoom Out"
                            >
                              <img
                                src={ZoomOutIcon}
                                alt="Zoom Out"
                                className="w-5 h-5"
                              />
                            </button>
                            <button
                              id={`${testId}-download`}
                              onClick={handleDownload}
                              className="p-2 rounded-full hover:bg-cfCardHover transition-colors"
                              title="Download"
                            >
                              <DownloadIcon
                                size={20}
                                className="inline-flex"
                                aria-hidden
                              />
                            </button>
                          </div>
                        </>
                      )}
                    </TransformWrapper>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
